import { TranslateConfigService } from 'src/app/services';
import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { StopwatchComponent } from '../components/stopwatch/stopwatch.component';
import { LocalNotification } from './local-notification.service';
import moment from 'src/app/utils/moment';
import { Subject } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { AppService } from './app.service';


@Injectable({
    providedIn: 'root'
})
export class StopwatchService {

    public defaultTime: number;
    public endTime: number;
    public fixTransform: any;
    public onFinish  = new Subject<any>();
    public percent: number = 100;
    public secondsRemaining: number;
    public status: "active" | "paused" | "inactive";
    public totalSeconds: number;
    public transform: any;
    public timeoutHolder: any;

    constructor(private appService: AppService,
        private localNotification: LocalNotification,
        private modalCtrl: ModalController,
        private sanitizer: DomSanitizer,
    private translateConfig: TranslateConfigService
  ) {
        this.initProgress();
     }

    isActive(): boolean {
        return this.status === "active";
    }

    isPaused(): boolean {
        return this.status === "paused";
    }

    async openStopwatch(time): Promise<any> {
        return new Promise(async (resolve, reject) => {
            const modal = await this.modalCtrl.create({
                component: StopwatchComponent,
                componentProps: { timeInSeconds: time },
                backdropDismiss: false,
                cssClass: 'stopwatchModal',
                showBackdrop: false,
            })
            modal.present();
            modal.onDidDismiss().then(result => {
                resolve(result)
            }).catch(err => reject(err))
        })
    }

    initProgress() {
        this.percent = 100;
        const increment = 180 / 100;
        const progress = 'rotate(' + increment * this.percent + 'deg)';
        this.transform = this.sanitizer.bypassSecurityTrustStyle(progress);
        this.fixTransform = this.sanitizer.bypassSecurityTrustStyle(progress);
    }
    

    startTimer(seconds: number) {
        this.totalSeconds = seconds;
        this.secondsRemaining = seconds;
        this.status = "active";
        this.localNotification.sendNotification(1, this.secondsRemaining);
        this.endTime = (moment as any)().add(this.secondsRemaining + 1, "seconds").valueOf();
        this.timerTick();
    }

    cancelTimer() {
        this.localNotification.cancelNotification(1);
        this.status = "inactive";
        clearTimeout(this.timeoutHolder);
        this.initProgress();
    }

    pauseTimer() {
        this.localNotification.cancelNotification(1);
        this.status = "paused";
    }

    resumeTimer() {
        this.endTime = (moment as any)().add(this.secondsRemaining + 1, "seconds").valueOf();
        this.localNotification.sendNotification(1, this.secondsRemaining);
        this.status = "active";
    }

    private timerTick() {
        this.timeoutHolder = setTimeout(() => {
            if(this.status === "inactive") {
                return;
            }
            if (this.status === "active") { 
                this.secondsRemaining = (this.endTime - (moment as any)().valueOf()) / 1000;
                this.percent = (this.secondsRemaining - 1) / this.totalSeconds * 100;
                const increment = 180 / 100;
                const progress = 'rotate(' + increment * this.percent + 'deg)';
                this.transform = this.sanitizer.bypassSecurityTrustStyle(progress);
                this.fixTransform = this.sanitizer.bypassSecurityTrustStyle(progress);
            }
            if (this.secondsRemaining >= 0) {
                this.timerTick();
            } else {
                this.initProgress();
                this.secondsRemaining = this.totalSeconds;
                this.onFinish.next({length: this.totalSeconds});
                this.defaultTime = this.totalSeconds;
                this.status = "inactive";
                this.playSound();
                this.playFinishHaptics();
                this.appService.showToastWithDuration(this.translateConfig.t('toasts.savedSuccess', { type: this.translateConfig.t('tracking.timer') }), null, "success");
            }
        }, 1000);
    }

    /** Timer complete sound – aggressive alarm-style beeps, repeated a few times. */
    private readonly timerCompleteTones = [
        { freq: 880, start: 0, duration: 0.12, volume: 0.55 },
        { freq: 880, start: 0.16, duration: 0.12, volume: 0.55 },
        { freq: 880, start: 0.32, duration: 0.12, volume: 0.55 },
        { freq: 1100, start: 0.5, duration: 0.14, volume: 0.6 },
        { freq: 1100, start: 0.68, duration: 0.14, volume: 0.6 },
    ];
    private readonly timerSoundRepeatCount = 3;
    private readonly timerSoundRepeatInterval = 1.8; // seconds between start of each repeat

    playSound(): void {
        try {
            const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
            if (!AudioContextClass) return;
            const audioContext = new AudioContextClass();
            const now = audioContext.currentTime;

            const playTone = (frequency: number, startTime: number, duration: number, volume: number = 0.5) => {
                const osc = audioContext.createOscillator();
                const gain = audioContext.createGain();

                osc.connect(gain);
                gain.connect(audioContext.destination);

                osc.frequency.value = frequency;
                osc.type = 'sine';

                gain.gain.setValueAtTime(0, startTime);
                gain.gain.linearRampToValueAtTime(volume, startTime + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

                osc.start(startTime);
                osc.stop(startTime + duration);
            };

            for (let r = 0; r < this.timerSoundRepeatCount; r++) {
                const offset = now + r * this.timerSoundRepeatInterval;
                this.timerCompleteTones.forEach(({ freq, start, duration, volume }) =>
                    playTone(freq, offset + start, duration, volume));
            }
        } catch (_) {
            // Audio context not available or user has disabled audio
        }
    }

    /** Trigger haptic feedback when timer completes (native only) – aggressive burst. */
    private playFinishHaptics(): void {
        if (!Capacitor.isNativePlatform()) return;
        const impact = () => Haptics.impact({ style: ImpactStyle.Heavy }).catch(() => {});
        impact();
        setTimeout(() => impact(), 120);
        setTimeout(() => impact(), 240);
        setTimeout(() => impact(), 360);
        setTimeout(() => impact(), 480);
        setTimeout(() => impact(), 600);
    }
}